import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { todoListFormSchema, type TodoListFormValues } from './todoListFormSchema'
import type { TodoList, TodoListUpsert } from '../types'

interface Props {
  visible: boolean
  /** Düzenlenen liste; `null` → yeni liste. */
  initial: TodoList | null
  submitting: boolean
  onSubmit: (payload: TodoListUpsert) => void
  onHide: () => void
}

/**
 * Notlarım — yeni liste / yeniden adlandırma formu (113).
 *
 * <p>Tek alanlı bir formdur ve paylaşılan `ui/FormDialog` + `Form*Field` katmanını kullanır (Reuse
 * Mandate); ekrana özel bir modal yazılmaz.
 *
 * <p>Güncellemede `modDate` gövdeye **eklenir** (optimistic lock): iki sekmede açık aynı liste
 * yeniden adlandırılırsa ikincisi 409 alır ve sessizce üzerine yazmaz.
 */
export default function TodoListFormDialog({
  visible,
  initial,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useZodForm(todoListFormSchema, {
    defaultValues: { name: '' },
  })

  useEffect(() => {
    if (visible) reset({ name: initial?.name ?? '' })
  }, [visible, initial, reset])

  const onValid = (v: TodoListFormValues) =>
    onSubmit({ name: v.name, modDate: initial?.modDate ?? null })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('todo.renameList') : t('todo.newList')}
      submitLabel={t('todo.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="name"
          label={t('todo.listName')}
          required
          maxLength={100}
        />
      </div>
    </FormDialog>
  )
}
